import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Box,
  AppBarProps,
  Divider,
  DividerProps,
  Typography,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
  Stack,
  Button,
  styled,
  SwitchProps,
  IconButton,
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

import {
  getConfigState,
  toggleAdminEditModel,
} from '../../../../store/ConfigSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { USER_PERMISSION } from '../../../../store/V2/RolesSlice';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { checkUserPermission } from '../../../../helper';
import NavigationOptionsIconButton from '../../../../components/Headers/NavigationOptionsIconButton';
import HeaderUserProfileOptionsViewer from './HeaderUserProfileOptionsViewer';
import CreditViewer from './CreditViewer';
import {
  chatDisconnectSocket,
  getChatState,
  initiChatConnection,
} from '../../../../store/V2/ChatSlice';
import UpgradeButton from './UpgradeButton';
import { getTenantsState } from '../../../../store/V2/TenantsSlice';

interface NavigationBarHeaderProps {
  appBarProps?: AppBarProps;
}

const dividerProps: DividerProps = {
  flexItem: true,
  orientation: 'vertical',
  variant: 'middle',
  sx: { margin: '16px', borderWidth: '1px', backgroundColor: '#00000033' },
};

export const EditModeSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#1E293B',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const getEditModeVisibility = (pathName: string) => {
  let isVisible = true;

  const isTutorListing = pathName.includes('portal/tutors');

  if (isTutorListing) {
    isVisible = false;
  }

  return isVisible;
};

const NavigationBarHeader: React.FC<NavigationBarHeaderProps> = ({
  appBarProps = {},
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const configState = useAppSelector(getConfigState);
  const chatState = useAppSelector(getChatState);

  const { appTenant } = tenantState;
  const { user: authUser } = authState;

  // State
  const [listenToSwitchChat, setListenToSwitchChat] = useState(false);

  const isSignInSignUpVisible =
    !authUser || (authUser && authUser.role.isGuest);

  const isUpgradeVisible =
    isSignInSignUpVisible || (authUser && authUser.role.name === 'Student');

  const redirectURL = location.pathname.replace('/learning-portal', '').trim();
  const classBookingUrl = `${process.env.REACT_APP_TUTOPIYA_PLATFORM_BASE_URL}/#/dashboard/consultee/consultant`;
  const isTrialRecordingsInUrl = window.location.href.includes(
    'trial-recording'
  );

  const isEditModeVisible = getEditModeVisibility(location.pathname);

  useEffect(() => {
    if (chatState.chatConnectionMode === undefined && listenToSwitchChat) {
      dispatch(initiChatConnection());
      setListenToSwitchChat(false);
    }
  }, [chatState.chatConnectionMode]);

  const toggleEditModel = async () => {
    dispatch(chatDisconnectSocket());
    dispatch(toggleAdminEditModel());
    setListenToSwitchChat(true);
  };

  return (
    <Box flexGrow={1} {...appBarProps}>
      <AppBar
        elevation={0}
        position="fixed"
        color="inherit"
        sx={{
          backgroundColor: TP_COLORS.white,
          boxShadow: '0px 4px 4px 0px #0000000F',
        }}
      >
        <Toolbar>
          <NavigationOptionsIconButton />
          <Box
            component={Link}
            to={`${process.env.PUBLIC_URL}/`}
            style={{ margin: '3px 0 0 0', padding: 0 }}
          >
            <img
              src={appTenant?.appLogoPath}
              alt="tp-logo"
              style={{ maxWidth: '130px', height: '33px' }}
            />
          </Box>
          {!isXsScreen && (
            <>
              <Divider {...dividerProps} />
              <Typography
                sx={{ fontWeight: 500, fontSize: '20px', color: '#003768' }}
              >
                Learning Platform
              </Typography>
            </>
          )}
          <span style={{ flexGrow: 1 }} />
          <Stack
            direction="row"
            alignItems="center"
            sx={{ display: { xs: 'none', md: 'flex' } }}
            gap={1}
          >
            <CreditViewer />
            {isSignInSignUpVisible && (
              <>
                <div>
                  <Button
                    disableElevation
                    variant="contained"
                    color="inherit"
                    style={{ color: TP_COLORS.black }}
                    onClick={() =>
                      navigate(
                        `${process.env.PUBLIC_URL}/login?${
                          redirectURL ? `redirect=${redirectURL}` : ''
                        }`
                      )
                    }
                  >
                    Sign in
                  </Button>
                </div>
                {!isTrialRecordingsInUrl && (
                  <div>
                    <Button
                      disableElevation
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        navigate(
                          `${process.env.PUBLIC_URL}/sign-up?${
                            redirectURL ? `redirect=${redirectURL}` : ''
                          }`
                        )
                      }
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
                {isTrialRecordingsInUrl && (
                  <div>
                    <Button
                      disableElevation
                      variant="contained"
                      color="primary"
                      href={classBookingUrl}
                      target="_blank"
                      startIcon={<CalendarTodayIcon />}
                    >
                      Book Classes With Tutopiya
                    </Button>
                  </div>
                )}
              </>
            )}
            {isUpgradeVisible && <UpgradeButton />}
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            {authUser &&
            isEditModeVisible &&
            checkUserPermission({
              authUser,
              tenant: appTenant,
              permissions: {
                adminCourse: USER_PERMISSION.ADMIN_COURSE,
              },
            }) ? (
              <>
                <FormControlLabel
                  label="Edit Mode"
                  labelPlacement="start"
                  sx={{ color: '#1E293B', textAlign: 'right' }}
                  control={
                    <EditModeSwitch
                      checked={configState.adminEditMode}
                      onChange={toggleEditModel}
                      sx={{ ml: '10px', mr: '20px' }}
                    />
                  }
                />
                {checkUserPermission({
                  authUser,
                  tenant: appTenant,
                  permissions: {
                    adminSettings: USER_PERMISSION.ADMIN_SETTINGS,
                  },
                }) && configState.adminEditMode ? (
                  <>
                    <IconButton
                      onClick={() =>
                        navigate(`${process.env.PUBLIC_URL}/portal/settings`)
                      }
                    >
                      <SettingsIcon sx={{ color: '#1E293B' }} />
                    </IconButton>
                  </>
                ) : null}
                {authUser && !authUser.role.isGuest ? (
                  <div
                    style={{
                      borderLeft: '2px solid #00000033',
                      minHeight: '32px',
                      marginRight: '5px',
                    }}
                  />
                ) : null}
              </>
            ) : null}
            {authUser && !authUser.role.isGuest ? (
              <HeaderUserProfileOptionsViewer />
            ) : null}
          </Stack>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default NavigationBarHeader;
